/**
 * Implied connections when creating strategy objects from a Strategy Canvas.
 * Rows are ordered: primary new object first, then what else is included (links,
 * canvas context, peer strategies).
 *
 * Future (parity with items): persist structural strategy/objective/goal/driver
 * changes in a dedicated lineage table (e.g. strategy_lineage_events) mirroring
 * items' lineage_events, so split/merge/supersede and parent links are auditable.
 */

export interface ImpliedConnectionRow {
  kind: string;
  label: string;
  detail?: string;
}

export function buildPreviewForNewStrategy(args: {
  newStrategyName: string;
  canvasTitle: string;
  /** Strategies already linked to this canvas (new one will appear with them). */
  otherLinkedStrategyNames: string[];
}): ImpliedConnectionRow[] {
  const rows: ImpliedConnectionRow[] = [
    {
      kind: 'New strategy',
      label: `"${args.newStrategyName}"`,
      detail: 'Creates this strategy in your workspace.',
    },
    {
      kind: 'Also linked to this canvas',
      label: args.canvasTitle || 'This canvas',
      detail: 'The strategy will be linked here so it shows in the Strategy area on this canvas.',
    },
  ];
  if (args.otherLinkedStrategyNames.length > 0) {
    rows.push({
      kind: 'You will still see on this canvas',
      label: args.otherLinkedStrategyNames.join(' · '),
      detail:
        'These strategies were already linked—they stay linked. Your new strategy appears alongside them in the same view.',
    });
  }
  return rows;
}

export function buildPreviewForObjectiveOrGoal(args: {
  kind: 'objective' | 'goal';
  name: string;
  canvasTitle: string;
  parentStrategyName: string | null;
  /** All strategy names currently linked to the canvas (context). */
  linkedStrategyNamesOnCanvas: string[];
}): ImpliedConnectionRow[] {
  const rows: ImpliedConnectionRow[] = [
    {
      kind: args.kind === 'goal' ? 'New goal' : 'New objective',
      label: `"${args.name}"`,
      detail: `Creates this ${args.kind} in your workspace.`,
    },
  ];
  if (args.parentStrategyName) {
    rows.push({
      kind: 'Also wired to',
      label: args.parentStrategyName,
      detail: `Scopes this ${args.kind} under the first strategy linked on this canvas so it shows in Objectives / Goals here.`,
    });
  } else {
    rows.push({
      kind: 'Strategy scope',
      label: 'No parent strategy on this canvas',
      detail: `No strategies are linked to this canvas yet. This ${args.kind} is created at the workspace level; it can still appear here when unscoped ${args.kind === 'goal' ? 'goals' : 'objectives'} are shown.`,
    });
  }
  if (args.linkedStrategyNamesOnCanvas.length > 0) {
    rows.push({
      kind: 'Canvas context',
      label: args.linkedStrategyNamesOnCanvas.join(' · '),
      detail: `These strategies remain on "${args.canvasTitle || 'this canvas'}"; the new ${args.kind} appears in context with them.`,
    });
  }
  return rows;
}

export function buildPreviewForOutcomeDriver(args: {
  title: string;
  canvasTitle: string;
  /** When true and a primary strategy exists, the driver record gets strategyId. */
  linkToPrimaryStrategy: boolean;
  /** Name of the first strategy on the canvas (if any). */
  primaryStrategyName: string | null;
  /** Whether this canvas has at least one linked strategy. */
  hasStrategiesOnCanvas: boolean;
  linkedStrategyNamesOnCanvas?: string[];
}): ImpliedConnectionRow[] {
  const rows: ImpliedConnectionRow[] = [
    {
      kind: 'New outcome driver',
      label: `"${args.title}"`,
      detail: 'Creates this outcome driver in your workspace.',
    },
  ];
  if (args.linkToPrimaryStrategy && args.primaryStrategyName) {
    rows.push({
      kind: 'Also wired to',
      label: args.primaryStrategyName,
      detail:
        'Sets the driver’s strategy reference to the first strategy linked on this canvas (aligned with the Strategy row).',
    });
  } else if (args.hasStrategiesOnCanvas) {
    rows.push({
      kind: 'Strategy link',
      label: 'Not linked to a strategy',
      detail:
        'You chose to keep this driver independent of the strategies on this canvas. You can attach a strategy later from Outcome drivers.',
    });
  } else {
    rows.push({
      kind: 'Strategy reference',
      label: 'No strategy link',
      detail:
        'No strategies are linked to this canvas yet; the driver is created without a strategy reference.',
    });
  }
  const onCanvas = args.linkedStrategyNamesOnCanvas?.filter(Boolean) ?? [];
  rows.push({
    kind: onCanvas.length > 0 ? 'Canvas context' : 'Where it appears',
    label: onCanvas.length > 0 ? onCanvas.join(' · ') : args.canvasTitle || 'This canvas',
    detail:
      onCanvas.length > 0
        ? 'Outcome drivers are workspace-wide. These strategies are on this canvas; the new driver still appears in this Drivers list.'
        : 'Outcome drivers are workspace-wide; this canvas still lists drivers you care about here.',
  });
  return rows;
}
